import React, { useState } from 'react';
import styled from 'styled-components';
import { useToast } from './ToastProvider';

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px 32px;
  max-width: 320px;
  max-height: 396px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${props => props.isOpen ? 'slideIn' : 'slideOut'} 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes slideOut {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: #e3f2fd;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 24px;
  color: #00A1FF0D;
  font-size: 24px;
`;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #404040;
  margin: 0 0 16px 0;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  text-align: center;
  margin: 0 0 24px 0;
  line-height: 1.4;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-weight:500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0288d1;
    box-shadow: 0 0 0 3px rgba(2, 136, 209, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 8px;
  margin-top: 28px;
`;

const Button = styled.button`
  padding: 10px 14px;
  border: none;
  width: 8rem;
  border-radius: 2rem;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;

  &:active {
    transform: scale(0.98);
  }
`;

const PrimaryButton = styled(Button)`
  background: #00a1ff;
  color: white;

  &:hover {
    background: #0090e5;
    box-shadow: 0 4px 8px rgba(2, 136, 209, 0.3);
  }

  &:disabled {
    background: #b3e5fc;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: #0288d1;
  font-weight: 500;

  // &:hover {
  //   background: rgba(2, 136, 209, 0.05);
  // }
`;

// Main Component
export const ClaimFetchModal = ({ 
    isOpen, 
    onClose, 
    onSubmit, 
    initialClaimID = 'C-874905'}) => {
  const [claimID, setClaimID] = useState(initialClaimID);
  const [isLoading, setIsSubmitting] = useState(false);
  const basePath = process.env.PUBLIC_URL || '';
  const {showToast} = useToast();

  const handleSubmit = async () => {
            if (!claimID?.trim()) {
       showToast({
          message: "Please enter a claim Id.",
          type: "warning"
        });
      return;
    }

    setIsSubmitting(true);
    
    // Call the onSubmit callback if provided
    if (onSubmit) {
      try {
        await onSubmit(claimID);
        // Modal will be closed by parent component after successful submission
      } catch (error) {
        console.error('Error submitting claim ID:', error);
        setIsSubmitting(false);
      }
    }
  };


  const handleCancel = () => {
    setClaimID(initialClaimID);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Overlay isOpen={isOpen}>
      <ModalContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <IconWrapper>
            <img src={`${basePath}/assets/images/workbench/data-icon-blue.png`} alt="star-icon"  />
        </IconWrapper>
        <Title>Enter a Claim Id <br /> to fetch claim details</Title>

        <FormGroup>
          <Label htmlFor="claimID">Claim ID</Label>
          <Input
            id="claimID"
            type="text"
            placeholder="Enter claim Id"
            value={claimID}
            onChange={(e) => setClaimID(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
        </FormGroup>

        <ButtonGroup>
          <PrimaryButton 
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Fetch Details'}
          </PrimaryButton>
          <SecondaryButton onClick={handleCancel} disabled={isLoading}>
            Cancel
          </SecondaryButton>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>
  );
};

export const ClaimSubmitModal = ({ 
    isOpen, 
    onClose, 
    onSubmit, 
}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const {showToast} = useToast();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Call the onSubmit callback if provided
    if (onSubmit) {
      try {
        await onSubmit();
        // Modal will be closed by parent component after successful submission
      } catch (error) {
        console.error('Error submitting claim ID:', error);
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Overlay isOpen={isOpen}>
      <ModalContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <Title>Are you sure you want to<br />submit this claim?</Title>
        <Subtitle>
          Once submitted, further changes may require reassignment or rework.
        </Subtitle>

        <ButtonGroup>
          <PrimaryButton 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'submitting...' : 'Submit'}
          </PrimaryButton>
          <SecondaryButton onClick={handleCancel}>
            Cancel
          </SecondaryButton>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>
  );
};
